/* eslint-disable max-lines */
/* eslint-disable max-lines-per-function */
import { useCallback, useRef } from 'react'

import * as turf from '@turf/turf'
import { inAndOut } from 'ol/easing'
import { extend, createEmpty } from 'ol/extent'
import GeoJSON from 'ol/format/GeoJSON'
import TileLayer from 'ol/layer/Tile'
import VectorLayer from 'ol/layer/Vector'
import VectorTile from 'ol/layer/VectorTile'
import WebGLTile from 'ol/layer/WebGLTile'
import { transformExtent } from 'ol/proj'
import TileWMS from 'ol/source/TileWMS'
import VectorSource from 'ol/source/Vector'
import { Circle, Fill, Stroke, Style } from 'ol/style'

import { useMap } from '../MapProvider'
import { detectProjection } from '../utils/projection-ol-util'

import type { Extent } from 'ol/extent'
import type Feature from 'ol/Feature'
import type { GeoJSONObject } from 'ol/format/GeoJSON'
import type { Geometry } from 'ol/geom'
import type Layer from 'ol/layer/Layer'
import type { StyleLike } from 'ol/style/Style'

// ---------------------------------------------------------------------------
// Internal constants
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Default style
// ---------------------------------------------------------------------------

const defaultStyle = new Style({
  fill: new Fill({ color: 'rgba(255,255,255,0.1)' }),
  stroke: new Stroke({ color: '#3399CC', width: 1.25 }),
  image: new Circle({ radius: 5, fill: new Fill({ color: '#3399CC' }) }),
})

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useLayers() {
  const { map } = useMap()

  // Registry for WMS layers with zoom requirements
  const wmsLayerRegistryRef = useRef<Map<string, Layer>>(new Map())

  const getLayer = useCallback(
    (layerId: string): Layer | undefined => {
      if (!map) {
        return undefined
      }
      return map.getAllLayers().find((layer) => layer.get('id') === layerId)
    },
    [map],
  )

  const setListeners = useCallback((layer: Layer): void => {
    layer.on('change:visible', () => {})
  }, [])

  const getLayerBySource = useCallback(
    (source: VectorSource, id: string, name: string): Layer =>
      new VectorLayer({ source, properties: { id, name }, style: defaultStyle }),
    [],
  )

  // Zoom to a single layer extent
  const zoomToLayer = useCallback(
    (layer: Layer | undefined, customPadding?: [number, number, number, number]): void => {
      if (!map || !layer) {
        return
      }

      if (layer instanceof VectorLayer) {
        const source = layer.getSource() as VectorSource

        const zoomToSource = () => {
          if (source.getFeatures().length) {
            const extent = source.getExtent()
            if (extent && extent.some((c) => c !== Infinity && c !== -Infinity)) {
              map.getView().fit(extent, {
                padding: customPadding || [100, 100, 100, 50],
                duration: 1400,
                easing: inAndOut,
              })
            }
          }
        }

        if (source?.getState() === 'ready') {
          zoomToSource()
          source?.once('change', zoomToSource)
        } else {
          source?.once('change', zoomToSource)
        }
      } else if (layer instanceof VectorTile) {
        const source = layer.getSource()
        const tryFit = (extent?: Extent) => {
          if (extent && extent.some((c) => c !== Infinity && c !== -Infinity)) {
            map.getView().fit(extent, {
              padding: customPadding || [100, 100, 100, 50],
              duration: 800,
              easing: inAndOut,
            })
          }
        }

        const extentFromLayer =
          (layer.get('extent') as Extent | undefined) || source?.getTileGrid()?.getExtent()
        if (extentFromLayer) {
          tryFit(extentFromLayer)
        }
      } else if (layer instanceof WebGLTile) {
        const source = layer.getSource()
        const zoomToRasterSource = async () => {
          try {
            const view = await source?.getView()
            if (view?.extent) {
              map.getView().fit(view.extent, {
                padding: customPadding || [100, 100, 100, 50],
                duration: 500,
              })
            }
          } catch {}
        }
        if (source) {
          source.once('change', zoomToRasterSource)
          setTimeout(zoomToRasterSource, 100)
        }
      } else if (layer instanceof TileLayer) {
        const source = layer.getSource()
        if (source instanceof TileWMS) {
          const storedExtent = layer.get('extent') as [number, number, number, number] | undefined
          if (storedExtent?.length === 4) {
            map.getView().fit(storedExtent, {
              padding: customPadding || [100, 100, 100, 50],
              duration: 500,
            })
          }
        }
      }
    },
    [map],
  )

  const removeLayer = useCallback(
    (layerId: string): void => {
      if (!map) {
        return
      }
      const layer = getLayer(layerId) || wmsLayerRegistryRef.current.get(layerId)
      if (layer) {
        map.removeLayer(layer)
        wmsLayerRegistryRef.current.delete(layerId)
      }
    },
    [map, getLayer],
  )

  const addLayer = useCallback(
    (layer: Layer, style?: StyleLike): void => {
      if (!map) {
        return
      }

      const layerId = layer.get('id')
      if (!layerId) {
        console.error("Layer must have an 'id' property.")
        return
      }

      removeLayer(layerId)

      if (layer instanceof VectorLayer) {
        const vectorLayer = layer as VectorLayer<VectorSource>
        vectorLayer.setStyle(style || defaultStyle)
        map.addLayer(layer)
        setListeners(layer)
      } else if (layer instanceof WebGLTile) {
        map.addLayer(layer)
        setListeners(layer)
      } else {
        map.addLayer(layer)
        setListeners(layer)
      }
    },
    [map, removeLayer, setListeners],
  )

  const addLayerByFeatures = useCallback(
    (
      features: Feature[],
      id: string,
      name: string,
      style?: StyleLike,
      initZoomToLayer?: boolean,
      zIndex?: number,
      declutter?: boolean,
    ): void => {
      if (!map) {
        return
      }

      const layerId = id || `Layer-id-${Date.now()}`
      removeLayer(layerId)

      const source = new VectorSource({ features })
      const layer = new VectorLayer({
        source,
        properties: { id: layerId, name: name || 'new layer' },
        style: style || defaultStyle,
        ...(zIndex !== undefined && { zIndex }),
        ...(declutter !== undefined && { declutter }),
      })

      map.addLayer(layer)
      setListeners(layer)
      if (initZoomToLayer) {
        zoomToLayer(layer, [100, 100, 100, 50])
      }
    },
    [map, removeLayer, setListeners, zoomToLayer],
  )

  const addLayerByGeoJSON = useCallback(
    (
      geoJSONObj: GeoJSONObject,
      id: string,
      name: string,
      style?: StyleLike,
      initZoomToLayer?: boolean,
      zIndex?: number,
      zoomPadding?: [number, number, number, number],
      declutter?: boolean,
    ): Promise<void> => {
      return new Promise((resolve) => {
        if (!map) {
          resolve()
          return
        }

        type GeoJSONWithCRS = GeoJSONObject & { crs?: { properties: { name: string } } }

        const { sourceProjection, featureProjection } = detectProjection(
          (geoJSONObj as GeoJSONWithCRS)?.crs?.properties?.name,
        )

        const features = new GeoJSON().readFeatures(geoJSONObj, {
          featureProjection,
          dataProjection: sourceProjection,
        })

        const source = new VectorSource({ features })
        const layer = new VectorLayer({
          source,
          properties: {
            id: id || `Layer-id-${Date.now()}`,
            name: name || 'new layer',
          },
          ...(zIndex !== undefined && { zIndex }),
          ...(declutter !== undefined && { declutter }),
        })

        const layerId = layer.get('id')
        if (!layerId) {
          console.error("Layer must have an 'id' property.")
          resolve()
          return
        }

        removeLayer(layerId)

        const resolveWhenZoomComplete = (): void => {
          const onMapReady = (): void => {
            map.un('rendercomplete', onMapReady)
            resolve()
          }
          map.once('rendercomplete', onMapReady)
          setTimeout(() => {
            map.un('rendercomplete', onMapReady)
            resolve()
          }, 2000)
        }

        if (layer instanceof VectorLayer) {
          const vectorLayer = layer as VectorLayer<VectorSource>
          vectorLayer.setStyle(style || defaultStyle)
          map.addLayer(layer)
          setListeners(layer)

          if (initZoomToLayer && source) {
            const attemptZoom = (): void => {
              if (source.getFeatures().length > 0) {
                zoomToLayer(layer, zoomPadding || [100, 100, 100, 50])
                resolveWhenZoomComplete()
              } else {
                resolve()
              }
            }

            map.once('rendercomplete', () => {
              if (source.getState() === 'ready') {
                attemptZoom()
              } else {
                source.once('change', attemptZoom)
              }
            })
          } else {
            resolve()
          }
        } else {
          map.addLayer(layer)
          setListeners(layer)
          resolve()
        }
      })
    },
    [map, removeLayer, setListeners, zoomToLayer],
  )

  const updateLayerStyle = useCallback(
    (layerId: string, style: StyleLike): void => {
      if (!map) {
        return
      }
      const layer = getLayer(layerId)
      if (layer instanceof VectorLayer) {
        layer.setStyle(style)
      }
    },
    [map, getLayer],
  )

  const addMultiLayers = useCallback(
    (layers: Layer[]): void => {
      if (!map) {
        return
      }
      layers.forEach((layer) => addLayer(layer))
    },
    [map, addLayer],
  )

  const addAndZoomToLayer = useCallback(
    (layer: Layer, style?: StyleLike): void => {
      if (!map) {
        return
      }
      addLayer(layer, style || defaultStyle)
      zoomToLayer(layer)
    },
    [map, addLayer, zoomToLayer],
  )

  const addTileLayer = useCallback(
    (layer: Layer) => {
      if (!map) {
        return
      }
      map.addLayer(layer)
      setListeners(layer)
    },
    [map, setListeners],
  )

  const addLayerBySource = useCallback(
    (source: VectorSource, id: string, name: string): void => {
      if (!map) {
        return
      }
      addLayer(getLayerBySource(source, id, name))
    },
    [map, addLayer, getLayerBySource],
  )

  const reorderLayers = useCallback(
    (layerIds: string[]): void => {
      if (!map) {
        return
      }
      const layers = map.getLayers()
      const layerArray = layers.getArray()
      const layerMap = new Map<string, Layer>()

      layerArray.forEach((layer) => {
        const id = layer.get('id')
        if (id) {
          layerMap.set(id, layer as Layer)
        }
      })

      const layersToReorder = layerIds
        .map((id) => layerMap.get(id))
        .filter((layer): layer is Layer => !!layer)

      layersToReorder.forEach((layer) => map.removeLayer(layer))
      layersToReorder.forEach((layer) => map.addLayer(layer))
    },
    [map],
  )

  const removeAllLayers = useCallback((): void => {
    if (!map) {
      return
    }
    map.getAllLayers().forEach((layer) => removeLayer(layer.get('id')))
  }, [map, removeLayer])

  const removeAllLayersExcept = useCallback(
    (layerId: string): void => {
      if (!map) {
        return
      }
      map.getAllLayers().forEach((layer) => {
        if (layer.get('id') !== 'basemap-layer' && layer.get('id') !== layerId) {
          removeLayer(layer.get('id'))
        }
      })
    },
    [map, removeLayer],
  )

  const hideLayer = useCallback(
    (layerId: string): void => {
      const layer = getLayer(layerId)
      if (layer) {
        layer.setVisible(false)
      }
    },
    [getLayer],
  )

  const hideAllLayers = useCallback((): void => {
    if (!map) {
      return
    }
    map.getAllLayers().forEach((layer) => hideLayer(layer.get('id')))
  }, [map, hideLayer])

  const hideLayersExcept = useCallback(
    (layers: Array<string>): void => {
      if (!map) {
        return
      }
      map.getAllLayers().forEach((layer) => {
        if (!layers.includes(layer.get('id'))) {
          hideLayer(layer.get('id'))
        }
      })
    },
    [map, hideLayer],
  )

  const showLayer = useCallback(
    (layerId: string): void => {
      const layer = getLayer(layerId)
      if (layer) {
        layer.setVisible(true)
      }
    },
    [getLayer],
  )

  const showAllLayers = useCallback((): void => {
    if (!map) {
      return
    }
    map.getAllLayers().forEach((layer) => showLayer(layer.get('id')))
  }, [map, showLayer])

  const isVisible = useCallback(
    (layerId: string): boolean => {
      const layer = getLayer(layerId)
      return layer ? layer.getVisible() : false
    },
    [getLayer],
  )

  const containsLayer = useCallback(
    (layerId: string): boolean => !!map?.getAllLayers().find((l) => l.get('id') === layerId),
    [map],
  )

  const zoomToAllLayers = useCallback((): void => {}, [])

  const zoomToLayerWithId = useCallback(
    (layerId: string, customPadding?: [number, number, number, number]) => {
      if (!map) {
        return
      }
      const layer = map.getAllLayers().find((l) => l.get('id') === layerId)
      if (layer) {
        zoomToLayer(layer, customPadding)
      }
    },
    [map, zoomToLayer],
  )

  const zoomToLayersWithIds = useCallback(
    (layerIds: string[], customPadding?: [number, number, number, number]) => {
      if (!map || !layerIds.length) {
        return
      }

      const combinedExtent = createEmpty()
      let hasValidExtent = false

      const layerLookup = new Map(map.getAllLayers().map((l) => [l.get('id'), l]))

      layerIds.forEach((id) => {
        const layer = layerLookup.get(id)
        if (layer instanceof VectorLayer) {
          const source = layer.getSource() as VectorSource
          if (source?.getFeatures().length > 0) {
            const extent = source.getExtent()
            if (extent && extent.some((c) => c !== Infinity && c !== -Infinity)) {
              extend(combinedExtent, extent)
              hasValidExtent = true
            }
          }
        }
      })

      if (hasValidExtent) {
        map.getView().fit(combinedExtent, {
          padding: customPadding || [100, 100, 100, 50],
          duration: 1400,
          easing: inAndOut,
        })
      }
    },
    [map],
  )

  const changeLayerColor = useCallback(
    (layerId: string, style?: StyleLike) => {
      if (!layerId) {
        return
      }
      const layer = getLayer(layerId)
      if (layer instanceof VectorLayer) {
        layer.setStyle(style)
      }
    },
    [getLayer],
  )

  const getAllLayers = useCallback((): Layer[] | undefined => {
    if (!map) {
      return undefined
    }
    return map.getAllLayers()
  }, [map])

  const getAllLayersFilterBase = useCallback((): Layer[] | undefined => {
    if (!map) {
      return undefined
    }
    return map.getAllLayers().filter((l) => l.get('id') !== 'basemap-layer')
  }, [map])

  const createLayerStyle = useCallback(
    (options: { strokeColor?: string; fillColor?: string; strokeWidth?: number }) => () =>
      new Style({
        fill: new Fill({ color: options.fillColor || 'rgba(255,255,255,0)' }),
        stroke: new Stroke({
          color: options.strokeColor || '#ffffff',
          width: options.strokeWidth || 2,
        }),
      }),
    [],
  )

  const getFeatures = useCallback(
    (layerId?: string): GeoJSON.FeatureCollection => {
      if (!map) {
        return { type: 'FeatureCollection', features: [] }
      }

      const allLayers = map.getLayers().getArray()
      const targetLayer = allLayers.find((layer) => {
        if (!(layer instanceof VectorLayer)) {
          return false
        }
        const source = layer.getSource()
        if (!(source instanceof VectorSource)) {
          return false
        }
        if (layerId) {
          return layer.get('id') === layerId
        }
        return source.getFeatures().length > 0 && !layer.get('id')
      }) as VectorLayer<VectorSource<Feature<Geometry>>> | undefined

      if (!targetLayer) {
        return { type: 'FeatureCollection', features: [] }
      }

      const source = targetLayer.getSource()
      if (!source) {
        return { type: 'FeatureCollection', features: [] }
      }

      const projection = map.getView().getProjection()
      const features = source.getFeatures()

      const featuresJson = features.map((feature) =>
        new GeoJSON().writeFeatureObject(feature, {
          featureProjection: projection,
          dataProjection: 'EPSG:4326',
        }),
      )

      return { type: 'FeatureCollection', features: featuresJson }
    },
    [map],
  )

  const zoomToBoundingBox = useCallback(
    (extent: [number, number, number, number]) => {
      if (!map) {
        return
      }
      map.getView().fit(extent, { padding: [50, 50, 50, 50], duration: 1000 })
    },
    [map],
  )

  const zoomToGeoJSON = useCallback(
    (geojson: GeoJSON.FeatureCollection | GeoJSON.Feature | null) => {
      if (!map || !geojson) {
        return
      }
      try {
        const features = new GeoJSON().readFeatures(geojson, {
          dataProjection: 'EPSG:4326',
          featureProjection: 'EPSG:3857',
        })
        if (!features.length) {
          return
        }

        const extent = new VectorSource({ features }).getExtent()
        if (extent && extent.some((c) => c !== Infinity && c !== -Infinity)) {
          map
            .getView()
            .fit(extent, { padding: [100, 100, 100, 50], duration: 800, easing: inAndOut })
        }
      } catch (error) {
        console.error('Error zooming to GeoJSON:', error)
      }
    },
    [map],
  )

  const zoomByFeatures = useCallback(
    (
      geoJsonFeatures: GeoJSON.Feature[],
      options?: { padding?: [number, number, number, number]; duration?: number; maxZoom?: number },
    ): void => {
      if (!map || !geoJsonFeatures?.length) {
        return
      }
      try {
        const fc = turf.featureCollection(geoJsonFeatures)
        const bbox = turf.bbox(fc)
        const extent = [bbox[0], bbox[1], bbox[2], bbox[3]] as [number, number, number, number]
        const mapExtent = transformExtent(extent, 'EPSG:4326', map.getView().getProjection())
        map.getView().fit(mapExtent, {
          padding: options?.padding ?? [5, 5, 5, 5],
          duration: options?.duration ?? 800,
          ...(options?.maxZoom != null && { maxZoom: options.maxZoom }),
          easing: inAndOut,
        })
      } catch (e) {
        console.error('Error zooming to features', e)
      }
    },
    [map],
  )

  /**
   * Manage WMS layer by zoom: add to map when zoom >= threshold, remove (cache) when below.
   */
  const manageWMSLayerByZoom = useCallback(
    (layerId: string, zoomLevelEnable: number): void => {
      if (!map) {
        return
      }
      const currentZoom = map.getView().getZoom()
      if (currentZoom === undefined) {
        return
      }

      const shouldBeVisible = currentZoom >= zoomLevelEnable
      const layer = getLayer(layerId)
      const isOnMap = layer !== null && layer !== undefined

      if (shouldBeVisible && !isOnMap) {
        const cachedLayer = wmsLayerRegistryRef.current.get(layerId)
        if (cachedLayer) {
          map.addLayer(cachedLayer)
          wmsLayerRegistryRef.current.delete(layerId)
        }
      } else if (!shouldBeVisible && isOnMap && layer) {
        map.removeLayer(layer)
        wmsLayerRegistryRef.current.set(layerId, layer)
      }
    },
    [map, getLayer],
  )

  /**
   * Add a WMS TileLayer to the map.
   */
  const addTileWMSLayer = useCallback(
    (
      id: string,
      name: string,
      url: string,
      layerName: string,
      options?: Record<string, unknown>,
      zoom?: boolean,
    ): void => {
      if (!map) {
        return
      }

      removeLayer(id)

      const { zoomLevelEnable, zIndex, ...wmsParams } = options ?? {}

      const source = new TileWMS({
        url,
        params: {
          LAYERS: layerName,
          ...wmsParams,
        },
        serverType: 'geoserver',
        crossOrigin: 'anonymous',
      })

      const layer = new TileLayer({
        source,
        properties: { id, name, type: 'wms' },
        ...(zIndex !== undefined && { zIndex: zIndex as number }),
      })

      if (zoomLevelEnable !== undefined) {
        const currentZoom = map.getView().getZoom()
        if (currentZoom !== undefined && currentZoom < (zoomLevelEnable as number)) {
          wmsLayerRegistryRef.current.set(id, layer)
          return
        }
      }

      map.addLayer(layer)
      setListeners(layer)

      if (zoom) {
        zoomToLayer(layer)
      }
    },
    [map, removeLayer, setListeners, zoomToLayer],
  )

  return {
    getLayer,
    addLayer,
    removeLayer,
    addLayerByGeoJSON,
    addLayerByFeatures,
    addLayerBySource,
    addTileLayer,
    addAndZoomToLayer,
    addMultiLayers,
    addTileWMSLayer,
    removeAllLayers,
    removeAllLayersExcept,
    updateLayerStyle,
    zoomToLayer,
    zoomToLayerWithId,
    zoomToLayersWithIds,
    zoomToAllLayers,
    zoomToGeoJSON,
    zoomByFeatures,
    zoomToBoundingBox,
    changeLayerColor,
    hideLayer,
    hideAllLayers,
    hideLayersExcept,
    showLayer,
    showAllLayers,
    isVisible,
    containsLayer,
    getAllLayers,
    getAllLayersFilterBase,
    reorderLayers,
    createLayerStyle,
    getFeatures,
    manageWMSLayerByZoom,
  }
}
